// DineInNotice.js
// First-visit informational pop-up clarifying dine-in service hours.
import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { Link } from 'react-router-dom';
import locationsData from './locationsData.js';
import './DineInNotice.scss';

const STORAGE_KEY = 'domenicos_dinein_notice_seen';

export default function DineInNotice() {
  const [open, setOpen] = useState(false);

  // Decide whether to show: once per session, and not on closed days.
  useEffect(() => {
    // Don't bake the modal into the react-snap pre-rendered HTML, or an
    // orphaned static copy would linger on the real page after hydration.
    if (/ReactSnap/i.test(navigator.userAgent)) return;

    let alreadySeen = false;
    try {
      alreadySeen = window.sessionStorage.getItem(STORAGE_KEY) === '1';
    } catch (e) {
      // sessionStorage unavailable (private mode, etc.) — fall through and show once.
    }
    if (alreadySeen) return;

    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const dayName = days[new Date().getDay()];
    const location = locationsData[0];
    const todayHours = location.hours.find((h) => h.day === dayName);
    const isClosedToday = !todayHours || /closed/i.test(todayHours.hours);
    if (isClosedToday) return;

    setOpen(true);
  }, []);

  const dismiss = (e) => {
    if (e) e.stopPropagation();
    try {
      window.sessionStorage.setItem(STORAGE_KEY, '1');
    } catch (err) {
      // ignore storage failures
    }
    setOpen(false);
  };

  // Close on ESC
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') dismiss();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // Prevent background scroll when open
  useEffect(() => {
    if (!open) return;
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = overflow || '';
    };
  }, [open]);

  if (!open) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) dismiss();
  };

  return ReactDOM.createPortal(
    <div
      className="dinein-notice__backdrop"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="dinein-notice-title"
    >
      <div className="dinein-notice__card">
        <div className="dinein-notice__icon" aria-hidden="true">🍽️</div>
        <h2 id="dinein-notice-title" className="dinein-notice__title">
          Dine-In Hours
        </h2>
        <p className="dinein-notice__lead">
          <strong>Dine-In service is available until 4 PM.</strong>
        </p>
        <p className="dinein-notice__text">
          After 4 PM, we offer <strong>Take-Out &amp; Delivery only</strong>.
        </p>
        <div className="dinein-notice__actions">
          <Link
            to="/pasadena/order"
            className="dinein-notice__cta dinein-notice__cta--primary"
            onClick={dismiss}
            autoFocus
          >
            Order Now
          </Link>
          <button
            type="button"
            className="dinein-notice__cta dinein-notice__cta--secondary"
            onClick={dismiss}
          >
            Got it
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
